import { IElementMeasurePolygon } from "@faro-lotv/ielement-types";
import { useAppSelector } from "@store/store-helper";
import { selectIElementWorldMatrix } from "@faro-lotv/project-source";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { getMarkupMeasurements } from "@utils/markups-utils";
import { Box, Stack } from "@mui/material";
import {
  Text,
  LengthIcon,
  PerimeterIcon,
  Area2Icon,
  DistanceHorizontalIcon,
  DistanceVerticalIcon,
  CoordinatesXIcon,
  CoordinatesYIcon,
  CoordinatesZIcon,
} from "@src/flat-ui";
import { MarkupMeasurement } from "@pages/project-details/project-markups/sidepanel/markup-measurement";

interface Props {
  /** the measure polygon element associated to the markup/annotation */
  measurePolygon: IElementMeasurePolygon;

  /** Unit of measurement to display the measurements in */
  unitMeasurement: SettingsApiTypes.UnitMeasurement;
}

/**
 * Renders different measurement values of a measure polygon element associated to an annotation
 */
export function MarkupMeasurements({
  measurePolygon,
  unitMeasurement,
}: Props): JSX.Element {
  const worldMatrix4Tuple = useAppSelector(
    selectIElementWorldMatrix(measurePolygon.id)
  );

  const { isClosed, length, area, horizontal, vertical, x, y, z } =
    getMarkupMeasurements({
      measurePolygon,
      worldMatrix4Tuple,
      unitMeasurement,
    });

  return (
    <Box sx={{ mt: "24px" }}>
      <Text variant="labelM">Measurement values</Text>
      <Stack
        sx={{
          mt: "16px",
          gap: 1,
        }}
      >
        {area && (
          <MarkupMeasurement value={area} tooltip="Area" icon={<Area2Icon />} />
        )}
        <MarkupMeasurement
          value={length}
          tooltip={isClosed ? "Perimeter" : "Length"}
          icon={isClosed ? <PerimeterIcon /> : <LengthIcon />}
        />
        {horizontal && (
          <MarkupMeasurement
            value={horizontal}
            tooltip="Horizontal"
            icon={<DistanceHorizontalIcon />}
          />
        )}
        {vertical && (
          <MarkupMeasurement
            value={vertical}
            tooltip="Vertical"
            icon={<DistanceVerticalIcon />}
          />
        )}
        {x && (
          <MarkupMeasurement
            value={x}
            tooltip="X"
            icon={<CoordinatesXIcon />}
          />
        )}
        {y && (
          <MarkupMeasurement
            value={y}
            tooltip="Y"
            icon={<CoordinatesYIcon />}
          />
        )}
        {z && (
          <MarkupMeasurement
            value={z}
            tooltip="Z"
            icon={<CoordinatesZIcon />}
          />
        )}
      </Stack>
    </Box>
  );
}
